import { useState, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Users,
  Plus,
  MoreHorizontal,
  Mail,
  Building2,
  Shield,
  CheckCircle,
  XCircle,
  UserPlus,
  Clock,
  Loader2,
  Pencil,
  UserX,
  Trash2,
  RefreshCw,
  Crown,
  Key,
  LockOpen,
  Send,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Pagination } from "@/components/ui/pagination";
import { UserSearchCombobox } from "@/components/ui/user-search-combobox";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { isSuperAdmin } from "@/lib/roles";
import { parseInviteEmails } from "@/lib/inviteEmails";
import { ReadOnlyBadge } from "@/components/portal/ReadOnly";
import {
  platformApi,
  PlatformUser,
  OrganisationMember,
  InvitationRoleOption,
  InviteMemberResultItem,
  Invitation,
  MAX_INVITE_EMAILS,
  MAX_INVITE_GROUPS,
} from "@/services/api/platform";
import { groupsApi, Group } from "@/services/api/groups";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { SortableTableHead } from "@/components/ui/sortable-table-head";

export const Route = createFileRoute("/portal/platform/users")({
  component: PlatformUsersPage,
});

/**
 * Row styling for one invite outcome.
 *
 * `updated` is deliberately its OWN colour rather than folded in with
 * `skipped`: something changed (the pending invitation's groups were
 * reconciled to this request), and an admin correcting a wrong group pick
 * must be able to see that it took effect.
 */
function getInviteResultClass(status: InviteMemberResultItem["status"]): string {
  const wrap = "min-w-0 basis-2/3 text-right break-words";
  switch (status) {
    case "invited":
      return "shrink-0 text-green-600";
    case "updated":
      return `${wrap} text-blue-600`;
    case "skipped":
      return `${wrap} text-amber-600`;
    default:
      return `${wrap} text-destructive`;
  }
}

interface CreateUserFormData {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
}

interface EditUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
}

// ---------------------------------------------------------------------------
// Organisation Members Tab
// ---------------------------------------------------------------------------

function OrganisationMembersTab({
  token,
  organisationId,
  organisationName,
  canManageTeam,
  isSuperAdmin,
}: {
  token: string | null;
  organisationId: string | undefined;
  organisationName: string;
  canManageTeam: boolean;
  isSuperAdmin: boolean;
}) {
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invitableRoles, setInvitableRoles] = useState<InvitationRoleOption[]>([]);
  // Backend-owned default invite role (no client-side role policy / positional guessing).
  const [defaultInviteRole, setDefaultInviteRole] = useState<string>("");
  const [inviteForm, setInviteForm] = useState<{
    emails: string;
    role: string;
  }>({
    emails: "",
    role: "",
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResults, setInviteResults] = useState<InviteMemberResultItem[] | null>(
    null,
  );
  // Groups the batch will join on acceptance. Empty by default: an invite that
  // grants nothing stays the default, it is not something you opt out of.
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [groupsLoadFailed, setGroupsLoadFailed] = useState(false);

  // Slice 2: add EXISTING members to a group in bulk. The invite dialog only
  // covers people who haven't joined yet; this is the same job for everyone
  // already in the org, which is where an admin lands after the first pass.
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [addToGroupIds, setAddToGroupIds] = useState<Set<string>>(new Set());
  const [addToGroupSubmitting, setAddToGroupSubmitting] = useState(false);

  // Add Existing User dialog state (Platform Admin only)
  const [addExistingDialogOpen, setAddExistingDialogOpen] = useState(false);
  const [addExistingForm, setAddExistingForm] = useState<{
    userId: string;
    email: string;
    role: "member" | "admin" | "owner";
  }>({
    userId: "",
    email: "",
    role: "member",
  });
  const [addExistingSubmitting, setAddExistingSubmitting] = useState(false);

  // Fetch available invitation roles from API
  useEffect(() => {
    if (!token) return;
    platformApi.getInvitationRoles(token).then(({ roles, default: defaultRole }) => {
      setInvitableRoles(roles);
      setDefaultInviteRole(defaultRole);
      setInviteForm((prev) => (prev.role ? prev : { ...prev, role: defaultRole }));
    }).catch(() => {
      // Backend unreachable — fall back to the same default the backend uses.
      setInvitableRoles([
        { value: "org_admin", label: "Admin" },
        { value: "org_member", label: "Member" },
      ]);
      setDefaultInviteRole("org_member");
      setInviteForm((prev) => (prev.role ? prev : { ...prev, role: "org_member" }));
    });
  }, [token]);

  // Groups available to assign at invite time. Loaded when either dialog opens
  // rather than on mount — most visits to this page never touch groups.
  useEffect(() => {
    if (!token || !organisationId || (!inviteDialogOpen && !addToGroupOpen)) return;
    groupsApi
      .listGroups(token, organisationId, { limit: 100, sort_by: "name", sort_order: "asc" })
      .then((response) => {
        setAvailableGroups(response.items ?? []);
        setGroupsLoadFailed(false);
      })
      .catch(() => {
        // Must NOT fail silently. An empty list renders identically to "this org
        // has no groups", so the admin would invite, grant nothing, and never be
        // told — the exact silent grant-miss this feature exists to remove.
        setAvailableGroups([]);
        setGroupsLoadFailed(true);
      });
  }, [token, organisationId, inviteDialogOpen, addToGroupOpen]);

  // Drop any member selection when the organisation changes. This tab stays
  // mounted while a platform admin switches orgs, so without the reset the
  // previous org's user ids would be submitted against THIS org's group.
  useEffect(() => {
    setSelectedMemberIds(new Set());
  }, [organisationId]);
  const [roleChangeSubmitting, setRoleChangeSubmitting] = useState<
    string | null
  >(null);
  const [removingMember, setRemovingMember] = useState<OrganisationMember | null>(
    null,
  );
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  // Server-side pagination with search
  const fetchMembers = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<OrganisationMember>> => {
      if (!token || !organisationId) return { data: [], totalCount: 0, page, pageSize };
      try {
        const response = await platformApi.listOrganisationMembers(token, organisationId, {
          offset: (page - 1) * pageSize,
          limit: pageSize,
          search: memberSearchTerm || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch {
        toast.error("Failed to load members");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, organisationId, memberSearchTerm, sortBy, sortOrder],
  );

  const {
    data: members,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<OrganisationMember>({
    fetchFn: fetchMembers,
    initialPageSize: 25,
    dependencies: [token, organisationId, memberSearchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  function getMemberName(member: OrganisationMember) {
    if (member.first_name && member.last_name)
      return `${member.first_name} ${member.last_name}`;
    if (member.first_name) return member.first_name;
    if (member.last_name) return member.last_name;
    return member.email;
  }

  const { emails: parsedInviteEmails, invalid: invalidInviteEntries } =
    parseInviteEmails(inviteForm.emails);
  const inviteOverCap = parsedInviteEmails.length > MAX_INVITE_EMAILS;
  const groupsOverCap = selectedGroupIds.size > MAX_INVITE_GROUPS;
  // Owners and admins bypass groups entirely (see cp01.Group). The selection is
  // still stored and applied, so it becomes live if they are later demoted —
  // but saying nothing here would let an admin believe access was granted.
  const roleBypassesGroups =
    inviteForm.role === "org_admin" || inviteForm.role === "org_owner";

  function toggleMemberSelected(memberId: string) {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  }

  function toggleAddToGroup(groupId: string) {
    setAddToGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  const pageMemberIds = members.map((m) => m.id);
  const allOnPageSelected =
    pageMemberIds.length > 0 && pageMemberIds.every((id) => selectedMemberIds.has(id));

  function toggleSelectAllOnPage() {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      // Scoped to the CURRENT page on purpose: the table is server-paginated, so
      // "select all" cannot honestly mean rows this client has never seen.
      if (allOnPageSelected) {
        pageMemberIds.forEach((id) => next.delete(id));
      } else {
        pageMemberIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  const handleAddMembersToGroups = async () => {
    if (!token || !organisationId) return;
    if (selectedMemberIds.size === 0 || addToGroupIds.size === 0) return;

    setAddToGroupSubmitting(true);
    const userIds = Array.from(selectedMemberIds);
    const groupIds = Array.from(addToGroupIds);
    try {
      // One request per GROUP carrying every user — the endpoint takes
      // `user_ids: []`, so this is |groups| requests, not |groups| x |users|.
      const results = await Promise.allSettled(
        groupIds.map((groupId) =>
          groupsApi.addGroupMembers(token, organisationId, groupId, userIds),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success(
          `${userIds.length} member${userIds.length === 1 ? "" : "s"} added to ` +
            `${groupIds.length} group${groupIds.length === 1 ? "" : "s"}`,
        );
        setAddToGroupOpen(false);
        setAddToGroupIds(new Set());
        setSelectedMemberIds(new Set());
      } else {
        // Never report a partial fan-out as success — say which part held.
        toast.error(
          `${groupIds.length - failed} of ${groupIds.length} groups updated. ` +
            "Re-open to retry the rest — adding someone twice is harmless.",
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      toast.error(message || "Failed to add members to groups");
    } finally {
      setAddToGroupSubmitting(false);
    }
  };

  function toggleInviteGroup(groupId: string) {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  const handleInvite = async () => {
    if (!token || !organisationId) return;
    if (parsedInviteEmails.length === 0) {
      toast.error("Enter at least one email address");
      return;
    }
    // The API would report these as failed rows rather than rejecting the
    // batch, so this is a courtesy: catch an obvious typo before it burns an
    // invitation slot and shows up in the results panel.
    if (invalidInviteEntries.length > 0) {
      toast.error("Fix or remove the highlighted entries first");
      return;
    }
    if (inviteOverCap) {
      toast.error(`You can invite at most ${MAX_INVITE_EMAILS} people at a time`);
      return;
    }
    if (groupsOverCap) {
      toast.error(`You can select at most ${MAX_INVITE_GROUPS} groups`);
      return;
    }
    setInviteSubmitting(true);
    setInviteResults(null);
    try {
      const response = await platformApi.inviteMember(token, organisationId, {
        emails: parsedInviteEmails,
        role: inviteForm.role,
        // OMIT rather than send [] when nothing is ticked. The dialog always
        // opens with a blank selection, so an admin re-inviting purely to chase
        // someone would otherwise send an explicit "clear" and wipe groups
        // chosen days earlier. Omitted means "no opinion"; clearing is done by
        // cancelling the pending invite.
        ...(selectedGroupIds.size > 0
          ? { group_ids: Array.from(selectedGroupIds) }
          : {}),
      });
      const { invited, updated, skipped, failed } = response.summary;

      // `updated` MUST be part of this gate. Without it an all-updated batch
      // took the clean-success path and toasted "0 invitations sent" while
      // discarding the per-row results — so the one row explaining that a
      // pending invitation's groups changed was unreachable in exactly the
      // case it mattered.
      if (updated === 0 && skipped === 0 && failed === 0) {
        toast.success(
          invited === 1
            ? `Invitation sent to ${response.results[0].email}`
            : `${invited} invitations sent`,
        );
        setInviteDialogOpen(false);
        setInviteForm({ emails: "", role: defaultInviteRole });
        setSelectedGroupIds(new Set());
      } else {
        // Keep the dialog open so the admin can see exactly which addresses
        // did not go out and why, rather than a single lossy toast.
        setInviteResults(response.results);
        if (invited > 0 || updated > 0) {
          // Count updates as successes in the numerator AND the denominator —
          // "2 of 5 sent" when 3 were group updates reads as 3 failures.
          const changed = invited + updated;
          const parts = [
            invited > 0 ? `${invited} sent` : null,
            updated > 0 ? `${updated} group update${updated === 1 ? "" : "s"}` : null,
          ].filter(Boolean);
          toast.success(
            `${parts.join(", ")} — ${changed} of ${response.results.length} addresses changed`,
          );
        } else {
          toast.error("No invitations were sent");
        }
      }
      refresh();
    } catch (err: unknown) {
      // Prefer PortalApiError.message: portalFetch already flattens a 422's
      // pydantic error array into a readable string, whereas details.detail
      // is still the raw array and renders as junk.
      const message = err instanceof Error ? err.message : "";
      toast.error(message || "Failed to send invitations");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleAddExistingUser = async () => {
    if (!token || !organisationId) return;
    if (!addExistingForm.email.trim()) {
      toast.error("Please select a user");
      return;
    }
    setAddExistingSubmitting(true);
    try {
      const result = await platformApi.addExistingUserToOrganisation(token, organisationId, {
        email: addExistingForm.email,
        role: addExistingForm.role,
      });
      toast.success(`${result.user.first_name || result.user.email} added to ${organisationName}`);
      setAddExistingDialogOpen(false);
      setAddExistingForm({ userId: "", email: "", role: "member" });
      refresh();
    } catch (err: unknown) {
      const detail = (err as { details?: { detail?: string } })?.details?.detail;
      toast.error(detail || "Failed to add user");
    } finally {
      setAddExistingSubmitting(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!token || !organisationId) return;
    setRoleChangeSubmitting(memberId);
    try {
      await platformApi.updateMemberRole(
        token,
        organisationId,
        memberId,
        newRole,
      );
      toast.success("Member role updated");
      refresh();
    } catch {
      toast.error("Failed to update member role");
    } finally {
      setRoleChangeSubmitting(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!token || !organisationId || !removingMember) return;
    setRemoveSubmitting(true);
    try {
      await platformApi.removeMember(token, organisationId, removingMember.id);
      toast.success(`${removingMember.first_name || removingMember.email} has been removed`);
      setRemovingMember(null);
      refresh();
    } catch {
      toast.error("Failed to remove member");
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const getMemberStatus = (member: OrganisationMember): string => {
    if (member.locked_until) {
      // Backend returns naive UTC timestamps — append Z so JS parses as UTC
      const lockedUntil = member.locked_until.endsWith("Z")
        ? member.locked_until
        : member.locked_until + "Z";
      if (new Date(lockedUntil) > new Date()) return "locked";
    }
    if (member.is_active === false) return "disabled";
    return "active";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case "locked":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Locked
          </Badge>
        );
      case "invited":
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Invited
          </Badge>
        );
      case "disabled":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Disabled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    const label = role.replace(/^org_/, "").replace(/^\w/, (c) => c.toUpperCase());
    const isPrivileged = ["owner", "admin"].includes(role.replace(/^org_/, ""));
    return <Badge variant={isPrivileged ? "default" : "secondary"}>{label}</Badge>;
  };

  const formatMemberDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={memberSearchTerm}
          onChange={(e) => setMemberSearchTerm(e.target.value)}
          onClear={() => setMemberSearchTerm("")}
          placeholder="Search members by name or email..."
          containerClassName="flex-1 min-w-[200px]"
        />
        <Button variant="outline" size="sm" onClick={refresh}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
        {isSuperAdmin && (
          <Button size="sm" variant="outline" onClick={() => setAddExistingDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add Existing User
          </Button>
        )}
        {canManageTeam && (
          <Button size="sm" onClick={() => setInviteDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Members
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              On This Page
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{members.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {members.filter((m) => m.role === "admin" || m.role === "owner").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {isLoading && members.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {memberSearchTerm ? "No members match your search" : "No members yet"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {memberSearchTerm ? "Try a different search term." : "Invite your first team member to get started."}
          </p>
          {!memberSearchTerm && canManageTeam && (
            <Button onClick={() => setInviteDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>
      ) : (
        <>
          {canManageTeam && selectedMemberIds.size > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-2">
              <span className="text-sm">
                {selectedMemberIds.size} member
                {selectedMemberIds.size === 1 ? "" : "s"} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMemberIds(new Set())}
                >
                  Clear
                </Button>
                <Button size="sm" onClick={() => setAddToGroupOpen(true)}>
                  <Users className="mr-2 h-4 w-4" />
                  Add to group
                </Button>
              </div>
            </div>
          )}
          <Table>
                <TableHeader>
                  <TableRow>
                    {canManageTeam && (
                      <TableHead className="w-[40px]">
                        <Checkbox
                          aria-label="Select all members on this page"
                          checked={allOnPageSelected}
                          onCheckedChange={toggleSelectAllOnPage}
                        />
                      </TableHead>
                    )}
                    <SortableTableHead
                      direction={getSortDirection("first_name")}
                      onSort={() => handleSort("first_name")}
                    >
                      Member
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("role")}
                      onSort={() => handleSort("role")}
                    >
                      Role
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("last_login_at")}
                      onSort={() => handleSort("last_login_at")}
                    >
                      Last Login
                    </SortableTableHead>
                    {canManageTeam && <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{""}</SortableTableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      {canManageTeam && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${getMemberName(member)}`}
                            checked={selectedMemberIds.has(member.id)}
                            onCheckedChange={() => toggleMemberSelected(member.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {getMemberName(member).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium">{getMemberName(member)}</div>
                            <div className="text-sm text-muted-foreground">
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(member.role)}</TableCell>
                      <TableCell>{getStatusBadge(getMemberStatus(member))}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatMemberDate(member.last_login_at)}
                      </TableCell>
                      {canManageTeam && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {member.role !== "owner" && (
                                <DropdownMenuItem
                                  disabled={roleChangeSubmitting === member.id}
                                  onClick={() =>
                                    handleRoleChange(
                                      member.id,
                                      member.role === "admin" ? "member" : "admin",
                                    )
                                  }
                                >
                                  <Shield className="mr-2 h-4 w-4" />
                                  {member.role === "admin"
                                    ? "Demote to Member"
                                    : "Promote to Admin"}
                                </DropdownMenuItem>
                              )}
                              {/* Owner transfer is sensitive — platform admins only.
                                  The API keeps the DB role and Casbin grouping in sync. */}
                              {isSuperAdmin && member.role !== "owner" && (
                                <DropdownMenuItem
                                  disabled={roleChangeSubmitting === member.id}
                                  onClick={() => handleRoleChange(member.id, "owner")}
                                >
                                  <Crown className="mr-2 h-4 w-4" />
                                  Set as Owner
                                </DropdownMenuItem>
                              )}
                              {isSuperAdmin && member.role === "owner" && (
                                <DropdownMenuItem
                                  disabled={roleChangeSubmitting === member.id}
                                  onClick={() => handleRoleChange(member.id, "admin")}
                                >
                                  <Shield className="mr-2 h-4 w-4" />
                                  Demote to Admin
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={async () => {
                                  if (!organisationId) return;
                                  try {
                                    await platformApi.sendPasswordReset(
                                      token!,
                                      organisationId,
                                      member.id,
                                    );
                                    toast.success(
                                      `Password reset email sent to ${member.email}`,
                                    );
                                  } catch {
                                    toast.error("Failed to send password reset email");
                                  }
                                }}
                              >
                                <Key className="mr-2 h-4 w-4" />
                                Reset Password
                              </DropdownMenuItem>
                              {getMemberStatus(member) === "locked" && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      await platformApi.unlockUser(
                                        token!,
                                        member.id,
                                      );
                                      toast.success(
                                        `Account unlocked for ${member.email}`,
                                      );
                                      refresh();
                                    } catch {
                                      toast.error("Failed to unlock account");
                                    }
                                  }}
                                >
                                  <LockOpen className="mr-2 h-4 w-4" />
                                  Unlock Account
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem disabled>
                                <Mail className="mr-2 h-4 w-4" />
                                Resend Invite
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setRemovingMember(member)}
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Remove Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
          </Table>
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={[10, 25, 50, 100]}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </>
      )}

      {/* Add existing members to groups (slice 2) */}
      <Dialog
        open={addToGroupOpen}
        onOpenChange={(open) => {
          setAddToGroupOpen(open);
          if (!open) setAddToGroupIds(new Set());
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to group</DialogTitle>
            <DialogDescription>
              {selectedMemberIds.size} selected member
              {selectedMemberIds.size === 1 ? "" : "s"} will be added to the groups
              you pick. Anyone already in a group is left as they are.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {groupsLoadFailed && (
              <p className="text-xs text-destructive">
                Couldn&apos;t load groups. Close and reopen to retry.
              </p>
            )}
            {!groupsLoadFailed && availableGroups.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This organisation has no groups yet. Create one under Groups
                first.
              </p>
            )}
            {availableGroups.length > 0 && (
              <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border p-3">
                {availableGroups.map((group) => (
                  <label
                    key={group.id}
                    className="flex cursor-pointer items-start gap-2 text-sm"
                  >
                    <Checkbox
                      checked={addToGroupIds.has(group.id)}
                      onCheckedChange={() => toggleAddToGroup(group.id)}
                    />
                    <span className="min-w-0">
                      <span className="block truncate">{group.name}</span>
                      {group.description && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {group.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToGroupOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddMembersToGroups}
              disabled={addToGroupSubmitting || addToGroupIds.size === 0}
            >
              {addToGroupSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add to {addToGroupIds.size || ""} group
              {addToGroupIds.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
        setInviteDialogOpen(open);
        if (!open) {
          setInviteResults(null);
          // Don't carry a previous batch's groups into the next invite.
          setSelectedGroupIds(new Set());
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Members</DialogTitle>
            <DialogDescription>
              Send invitations to join {organisationName}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-emails">Email Addresses</Label>
              <Textarea
                id="invite-emails"
                rows={4}
                placeholder={"user@example.com\nanother@example.com"}
                value={inviteForm.emails}
                onChange={(e) => {
                  setInviteForm({ ...inviteForm, emails: e.target.value });
                  setInviteResults(null);
                }}
              />
              <p
                className={
                  inviteOverCap
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {inviteOverCap
                  ? `${parsedInviteEmails.length} addresses — at most ${MAX_INVITE_EMAILS} at a time`
                  : `Separate with commas or new lines. ${parsedInviteEmails.length} address${
                      parsedInviteEmails.length === 1 ? "" : "es"
                    } entered.`}
              </p>
              {invalidInviteEntries.length > 0 && (
                <p className="text-xs text-destructive">
                  Not a valid email address: {invalidInviteEntries.join(", ")}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) =>
                  setInviteForm({ ...inviteForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {invitableRoles.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Applies to everyone in this batch.
              </p>
            </div>
            {groupsLoadFailed && (
              <p className="text-xs text-destructive">
                Couldn&apos;t load groups — this invitation will not add anyone to
                a group. Close and reopen to retry.
              </p>
            )}
            {/* Hidden entirely when the org has no groups — an empty box would
                just raise a question the admin can't act on from here. */}
            {availableGroups.length > 0 && (
              <div className="space-y-2">
                <Label>Groups</Label>
                <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border p-3">
                  {availableGroups.map((group) => (
                    <label
                      key={group.id}
                      className="flex cursor-pointer items-start gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedGroupIds.has(group.id)}
                        onCheckedChange={() => toggleInviteGroup(group.id)}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{group.name}</span>
                        {group.description && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {group.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
                <p
                  className={
                    groupsOverCap
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {groupsOverCap
                    ? `${selectedGroupIds.size} groups — at most ${MAX_INVITE_GROUPS}`
                    : "Optional. Everyone in this batch joins these groups when they accept."}
                </p>
                {roleBypassesGroups && selectedGroupIds.size > 0 && (
                  <p className="text-xs text-amber-600">
                    Admins and owners already have full access, so groups have no
                    effect for them. The selection is kept and applies if their
                    role is lowered later.
                  </p>
                )}
              </div>
            )}
            {inviteResults && (
              <div className="space-y-1 rounded-lg border p-3 max-h-56 overflow-y-auto">
                {inviteResults.map((result) => (
                  <div
                    key={result.email}
                    className="flex items-start justify-between gap-3 text-sm"
                  >
                    <span className="truncate">{result.email}</span>
                    {/* Reasons run long (the sole-owner error is ~150 chars),
                        so wrap rather than clip — a truncated explanation the
                        user cannot scroll to is no explanation. */}
                    <span
                      className={getInviteResultClass(result.status)}
                    >
                      {result.status === "invited" ? "Sent" : result.error}
                    </span>
                  </div>
                ))}
                <p className="pt-2 text-xs text-muted-foreground">
                  Already-invited people can be resent from the Pending Invites tab.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteDialogOpen(false)}
            >
              {inviteResults ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={handleInvite}
              disabled={
                inviteSubmitting ||
                inviteOverCap ||
                groupsOverCap ||
                invalidInviteEntries.length > 0 ||
                parsedInviteEmails.length === 0
              }
            >
              {inviteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {parsedInviteEmails.length > 1
                ? `Send ${parsedInviteEmails.length} Invitations`
                : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Existing User Dialog (Platform Admin only) */}
      {isSuperAdmin && (
        <Dialog open={addExistingDialogOpen} onOpenChange={setAddExistingDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Existing User</DialogTitle>
              <DialogDescription>
                Add an existing platform user to {organisationName} without sending an invitation email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select User</Label>
                <UserSearchCombobox
                  token={token}
                  value={addExistingForm.userId}
                  onValueChange={(userId, user) =>
                    setAddExistingForm({
                      ...addExistingForm,
                      userId,
                      email: user?.email || "",
                    })
                  }
                  placeholder="Search for a user..."
                  searchPlaceholder="Type name or email..."
                  emptyMessage="No users found. Try a different search term."
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={addExistingForm.role}
                  onValueChange={(value: "member" | "admin" | "owner") =>
                    setAddExistingForm({ ...addExistingForm, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAddExistingDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddExistingUser}
                disabled={addExistingSubmitting || !addExistingForm.email}
              >
                {addExistingSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Add User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Remove Member Confirmation */}
      <AlertDialog
        open={!!removingMember}
        onOpenChange={(open) => !open && setRemovingMember(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {removingMember?.email} from {organisationName}? They will lose
              access to all organisation resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removeSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Invites Tab
// ---------------------------------------------------------------------------

const DATE_FORMAT = new Intl.DateTimeFormat("en-ZA", {
  dateStyle: "medium",
});

function PendingInvitesTab({
  token,
  organisationId,
  organisationName,
}: {
  token: string | null;
  organisationId: string | undefined;
  organisationName: string;
}) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingInvitation, setCancellingInvitation] = useState<Invitation | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const fetchInvitations = useCallback(async () => {
    if (!token || !organisationId) return;
    setIsLoading(true);
    try {
      const response = await platformApi.listInvitations(token, organisationId, {
        status: "pending",
      });
      setInvitations(response.items);
    } catch {
      toast.error("Failed to load pending invitations");
    } finally {
      setIsLoading(false);
    }
  }, [token, organisationId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleResend = async (invitation: Invitation) => {
    if (!token || !organisationId) return;
    setResendingId(invitation.id);
    try {
      await platformApi.resendInvitation(token, organisationId, invitation.id);
      toast.success(`Invitation resent to ${invitation.email}`);
      fetchInvitations();
    } catch {
      toast.error("Failed to resend invitation");
    } finally {
      setResendingId(null);
    }
  };

  const handleCancel = async () => {
    if (!token || !organisationId || !cancellingInvitation) return;
    setCancelSubmitting(true);
    try {
      await platformApi.cancelInvitation(token, organisationId, cancellingInvitation.id);
      toast.success(`Invitation to ${cancellingInvitation.email} cancelled`);
      setCancellingInvitation(null);
      fetchInvitations();
    } catch {
      toast.error("Failed to cancel invitation");
    } finally {
      setCancelSubmitting(false);
    }
  };

  function formatDate(dateString: string | null) {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? "—" : DATE_FORMAT.format(date);
  }

  function formatRole(role: string) {
    return role.replace(/^org_/, "").replace(/^\w/, (c) => c.toUpperCase());
  }

  const pendingCount = invitations.filter((i) => !i.is_expired).length;
  const expiredCount = invitations.filter((i) => i.is_expired).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Expired
            </CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{expiredCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Invitations
            </CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invitations.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => fetchInvitations()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {invitations.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No pending invitations</h3>
          <p className="text-muted-foreground">
            All invitations have been accepted or expired. Use &quot;Invite Member&quot; on the Members tab to send new invitations.
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead>Invited By</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Resends</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">
                    {invitation.email}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatRole(invitation.role)}</Badge>
                  </TableCell>
                  {/* The grant is otherwise invisible until the person accepts,
                      which is how a wrong one goes unnoticed for a week. */}
                  <TableCell>
                    {invitation.groups?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {invitation.groups.map((group) => (
                          <Badge key={group.id} variant="secondary">
                            {group.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invitation.inviter_name || invitation.inviter_email || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(invitation.created_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(invitation.expires_at)}
                  </TableCell>
                  <TableCell>
                    {invitation.is_expired ? (
                      <Badge variant="destructive">Expired</Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">
                    {invitation.resend_count}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={resendingId === invitation.id}
                          onClick={() => handleResend(invitation)}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {resendingId === invitation.id ? "Resending..." : "Resend Invitation"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setCancellingInvitation(invitation)}
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Cancel Invitation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Cancel Confirmation */}
      <AlertDialog
        open={!!cancellingInvitation}
        onOpenChange={(open) => !open && setCancellingInvitation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for{" "}
              <span className="font-medium">{cancellingInvitation?.email}</span>?
              They will no longer be able to join {organisationName} using this
              invitation link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSubmitting}>
              Keep Invitation
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// All Platform Users Tab (superadmin only)
// ---------------------------------------------------------------------------

function AllPlatformUsersTab({
  token,
}: {
  token: string | null;
}) {
  const { session } = useAuth();
  // Lowercase for case-insensitive comparison: the User.Email column uses a
  // case-insensitive collation, so two rows could differ only in case and
  // strict equality would miss the self-row.
  const currentEmail = session?.user?.email?.toLowerCase() ?? null;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
  const [deactivatingUser, setDeactivatingUser] = useState<PlatformUser | null>(
    null,
  );

  // Form state
  const [createFormData, setCreateFormData] = useState<CreateUserFormData>({
    email: "",
    username: "",
    password: "",
    first_name: "",
    last_name: "",
  });
  const [editFormData, setEditFormData] = useState<EditUserFormData>({
    first_name: "",
    last_name: "",
    email: "",
    is_active: true,
    is_admin: false,
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [deletingUser, setDeletingUser] = useState<PlatformUser | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Server-side paginated data fetch
  const fetchUsers = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<PlatformUser>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await platformApi.listUsers(token, {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, searchTerm, sortBy, sortOrder],
  );

  const {
    data: users,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<PlatformUser>({
    fetchFn: fetchUsers,
    initialPageSize: 25,
    dependencies: [token, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return formatDate(dateString);
  };

  // Form handlers
  const resetCreateForm = () => {
    setCreateFormData({
      email: "",
      username: "",
      password: "",
      first_name: "",
      last_name: "",
    });
  };

  const openCreateDialog = () => {
    resetCreateForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (user: PlatformUser) => {
    setEditFormData({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email,
      is_active: user.is_active,
      is_admin: user.is_admin,
    });
    setEditingUser(user);
  };

  const closeDialogs = () => {
    setShowCreateDialog(false);
    setEditingUser(null);
    resetCreateForm();
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormSubmitting(true);
    try {
      await platformApi.createUser(token, {
        email: createFormData.email,
        username: createFormData.username || createFormData.email,
        password: createFormData.password,
        first_name: createFormData.first_name || undefined,
        last_name: createFormData.last_name || undefined,
      });
      toast.success("User created successfully");
      closeDialogs();
      refresh();
    } catch {
      toast.error("Failed to create user");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !editingUser) return;

    const trimmedEmail = editFormData.email.trim();
    if (!trimmedEmail) {
      toast.error("Email is required");
      return;
    }

    setFormSubmitting(true);
    try {
      await platformApi.updateUser(token, editingUser.id, {
        first_name: editFormData.first_name || undefined,
        last_name: editFormData.last_name || undefined,
        // Only send email if it changed, so an unchanged value doesn't trip
        // a duplicate-email check on the user's own current address.
        email:
          trimmedEmail !== editingUser.email ? trimmedEmail : undefined,
        is_active: editFormData.is_active,
        is_admin: editFormData.is_admin,
      });
      toast.success("User updated successfully");
      closeDialogs();
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update user",
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !deletingUser) return;

    setDeleteSubmitting(true);
    try {
      await platformApi.deleteUser(token, deletingUser.id);
      toast.success("User deleted");
      setDeletingUser(null);
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete user",
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!token || !deactivatingUser) return;

    setDeactivateSubmitting(true);
    try {
      await platformApi.updateUser(token, deactivatingUser.id, {
        is_active: false,
      });
      toast.success("User deactivated successfully");
      setDeactivatingUser(null);
      refresh();
    } catch {
      toast.error("Failed to deactivate user");
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Failed to load users: {error}</p>
        <Button onClick={refresh} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          placeholder="Search users..."
          containerClassName="flex-1 min-w-[200px]"
        />
        <Button variant="outline" size="sm" onClick={refresh}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
        <Button size="sm" onClick={openCreateDialog}>
          <UserPlus className="mr-2 h-4 w-4" />
          Create User
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {searchTerm ? "No users match your search" : "No users yet"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm ? "Try a different search term." : "Create your first user to get started."}
          </p>
          {!searchTerm && (
            <Button onClick={openCreateDialog}>
              <UserPlus className="h-4 w-4 mr-2" />
              Create User
            </Button>
          )}
        </div>
      ) : (
        <>
          <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={getDirection("first_name")} onSort={() => handleSort("first_name")}>User</SortableTableHead>
                    <SortableTableHead direction={getDirection("is_admin")} onSort={() => handleSort("is_admin")}>Status</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>Organisations</SortableTableHead>
                    <SortableTableHead direction={getDirection("last_login_at")} onSort={() => handleSort("last_login_at")}>Last Login</SortableTableHead>
                    <SortableTableHead direction={getDirection("created_at")} onSort={() => handleSort("created_at")}>Created</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{""}</SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const isSelf =
                      currentEmail !== null &&
                      user.email.toLowerCase() === currentEmail;
                    return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {(
                                user.first_name?.[0] || user.email[0]
                              ).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {user.first_name && user.last_name
                                ? `${user.first_name} ${user.last_name}`
                                : user.username}
                              {user.is_admin && (
                                <Badge variant="default" className="text-xs">
                                  <Shield className="h-3 w-3 mr-1" />
                                  Admin
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant={user.is_active ? "success" : "destructive"}
                          >
                            {user.is_active ? (
                              <span className="flex items-center">
                                <CheckCircle className="h-3 w-3 mr-1" /> Active
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <XCircle className="h-3 w-3 mr-1" /> Inactive
                              </span>
                            )}
                          </Badge>
                          {!user.email_verified && (
                            <Badge variant="outline" className="text-xs">
                              Email unverified
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.organisations && user.organisations.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.organisations.slice(0, 2).map((org) => (
                              <Badge
                                key={org.organisation_id}
                                variant="outline"
                                className="text-xs"
                              >
                                <Building2 className="h-3 w-3 mr-1" />
                                {org.organisation_name}
                              </Badge>
                            ))}
                            {user.organisations.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{user.organisations.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            No organisations
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span
                            className={`text-sm ${user.last_login_at ? "" : "text-muted-foreground"}`}
                            title={
                              user.last_login_at
                                ? new Date(user.last_login_at).toLocaleString()
                                : "Never logged in"
                            }
                          >
                            {formatRelativeTime(user.last_login_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openEditDialog(user)}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.is_active ? (
                              <DropdownMenuItem
                                onClick={() => setDeactivatingUser(user)}
                                className="text-destructive focus:text-destructive"
                              >
                                <UserX className="h-4 w-4 mr-2" />
                                Deactivate User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={async () => {
                                  if (!token) return;
                                  try {
                                    await platformApi.updateUser(
                                      token,
                                      user.id,
                                      { is_active: true },
                                    );
                                    toast.success("User activated");
                                    refresh();
                                  } catch {
                                    toast.error("Failed to activate user");
                                  }
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Activate User
                              </DropdownMenuItem>
                            )}
                            {!isSelf && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setDeletingUser(user)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

          {/* Pagination */}
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </>
      )}

      {/* Create User Dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => !open && closeDialogs()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Create a new user account on the platform.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={createFormData.email}
                  onChange={(e) =>
                    setCreateFormData({
                      ...createFormData,
                      email: e.target.value,
                      username: e.target.value,
                    })
                  }
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={createFormData.password}
                  onChange={(e) =>
                    setCreateFormData({
                      ...createFormData,
                      password: e.target.value,
                    })
                  }
                  placeholder="Enter password"
                  required
                  minLength={8}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={createFormData.first_name}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        first_name: e.target.value,
                      })
                    }
                    placeholder="John"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={createFormData.last_name}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        last_name: e.target.value,
                      })
                    }
                    placeholder="Doe"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialogs}>
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information for {editingUser?.email}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_first_name">First Name</Label>
                  <Input
                    id="edit_first_name"
                    value={editFormData.first_name}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        first_name: e.target.value,
                      })
                    }
                    placeholder="John"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit_last_name">Last Name</Label>
                  <Input
                    id="edit_last_name"
                    value={editFormData.last_name}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        last_name: e.target.value,
                      })
                    }
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      email: e.target.value,
                    })
                  }
                  placeholder="user@example.com"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Must be unique across the platform.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_active"
                  checked={editFormData.is_active}
                  onCheckedChange={(checked) =>
                    setEditFormData({
                      ...editFormData,
                      is_active: checked === true,
                    })
                  }
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_admin"
                  checked={editFormData.is_admin}
                  onCheckedChange={(checked) =>
                    setEditFormData({
                      ...editFormData,
                      is_admin: checked === true,
                    })
                  }
                />
                <Label htmlFor="is_admin">Administrator</Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingUser(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog
        open={!!deactivatingUser}
        onOpenChange={(open) => !open && setDeactivatingUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {deactivatingUser?.email}?
              They will no longer be able to log in to the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              disabled={deactivateSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deactivateSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Deactivate User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingUser}
        onOpenChange={(open) => !open && setDeletingUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deletingUser?.email} and any
              organisation memberships, sessions, API keys, and notifications
              they own. Audit history will be preserved. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

function PlatformUsersPage() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const canManageTeam = can("manage", "team");
  const isUserSuperAdmin = isSuperAdmin(session);

  const organisationId = selectedOrganisation?.organisationId;
  const organisationName =
    selectedOrganisation?.organisationName || "your organisation";

  const pageTitle = isUserSuperAdmin ? "Members & Users" : "Members";

  // Non-superadmin: show only the members content
  if (!isUserSuperAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">{pageTitle}</h1>
            <p className="mt-2 text-lg text-slate-600">
              Manage members of {organisationName}
            </p>
          </div>
          <ReadOnlyBadge show={!canManageTeam} />
        </div>

        {/* Content Panel */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          {canManageTeam ? (
            <Tabs defaultValue="members">
              <TabsList>
                <TabsTrigger value="members">Organisation Members</TabsTrigger>
                <TabsTrigger value="pending-invites">Pending Invites</TabsTrigger>
              </TabsList>
              <TabsContent value="members" className="mt-6">
                <OrganisationMembersTab
                  token={token}
                  organisationId={organisationId}
                  organisationName={organisationName}
                  canManageTeam={canManageTeam}
                  isSuperAdmin={false}
                />
              </TabsContent>
              <TabsContent value="pending-invites" className="mt-6">
                <PendingInvitesTab
                  token={token}
                  organisationId={organisationId}
                  organisationName={organisationName}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <OrganisationMembersTab
              token={token}
              organisationId={organisationId}
              organisationName={organisationName}
              canManageTeam={canManageTeam}
              isSuperAdmin={false}
            />
          )}
        </div>
      </div>
    );
  }

  // Superadmin: show tabs with both views
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="mt-2 text-lg text-slate-600">
          Manage members of {organisationName}
        </p>
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members">Organisation Members</TabsTrigger>
            <TabsTrigger value="pending-invites">Pending Invites</TabsTrigger>
            <TabsTrigger value="all-users">All Platform Users</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-6">
            <OrganisationMembersTab
              token={token}
              organisationId={organisationId}
              organisationName={organisationName}
              canManageTeam={canManageTeam}
              isSuperAdmin={isUserSuperAdmin}
            />
          </TabsContent>

          <TabsContent value="pending-invites" className="mt-6">
            <PendingInvitesTab
              token={token}
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </TabsContent>

          <TabsContent value="all-users" className="mt-6">
            <AllPlatformUsersTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
